import React from "react";
import { FormControl } from "react-bootstrap";
import { ApiHelper, ScheduleHelper, ScheduleTimeInterface, ToastHelper } from "../../helpers";

interface Props {
  scheduleId: number,
  scheduleTimes: ScheduleTimeInterface[],
  saveCallback: (scheduleId: number) => void,
  pendingCommand: { action: string, parameter: string }
}

export const ScheduleTimes: React.FC<Props> = props => {

  const [times, setTimes] = React.useState<ScheduleTimeInterface[]>(null);
  const [deleteIds, setDeleteIds] = React.useState<number[]>([]);


  const init = () => {
    setTimes(props.scheduleTimes);
  }

  const getDayOptions = () => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const result: React.ReactElement[] = []
    for (let i = 0; i < days.length; i++) result.push(<option value={i}>{days[i]}</option>);
    return result;
  }


  const handleChange = (e: any, index: number) => {
    let timesCopy: ScheduleTimeInterface[] = [...times];
    let t = timesCopy[index];

    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "dayOfWeek": t.dayOfWeek = parseInt(val, 0); break;
      case "startTime":
      case "endTime":
        const { hours, minutes } = ScheduleHelper.getHourMinute(e.currentTarget.value);
        if (e.currentTarget.id === "startTime") {
          t.startHour = hours;
          t.startMinute = minutes;
        } else {
          t.endHour = hours;
          t.endMinute = minutes;
        }
        break;

    }
    setTimes(timesCopy);
  }

  const handleDelete = (index: number) => {
    const t = times[index];

    if (t.id && deleteIds.indexOf(t.id) === -1) {
      let deleteIdsCopy: number[] = [...deleteIds];
      deleteIdsCopy.push(t.id)
      setDeleteIds(deleteIdsCopy);
    }

    let timesCopy: ScheduleTimeInterface[] = [...times];
    timesCopy.splice(index, 1);
    setTimes(timesCopy);
  }

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    let timesCopy: ScheduleTimeInterface[] = [...times];
    let dayOfWeek = 0;
    if (times.length > 0 && times[times.length - 1].dayOfWeek < 6) dayOfWeek = times[times.length - 1].dayOfWeek + 1;
    timesCopy.push({ dayOfWeek: dayOfWeek, endHour: 17, endMinute: 0, startHour: 8, startMinute: 0, scheduleId: props.scheduleId });
    setTimes(timesCopy);
  }

  const getRows = () => {
    const result: React.ReactElement[] = [];
    if (times) {
      let idx = 0;
      for (let t of times) {
        const index = idx;
        const changeFunction = (e: any) => { handleChange(e, index); }
        result.push(
          <tr key={"st_" + t.id + "_" + index}>
            <td><FormControl as="select" size="sm" id="dayOfWeek" value={t.dayOfWeek} onChange={changeFunction} >{getDayOptions()}</FormControl></td>
            <td><FormControl type="time" size="sm" id="startTime" value={ScheduleHelper.getHtmlTime(t.startHour, t.startMinute)} onChange={changeFunction} ></FormControl></td>
            <td><FormControl type="time" size="sm" id="endTime" value={ScheduleHelper.getHtmlTime(t.endHour, t.endMinute)} onChange={changeFunction} ></FormControl></td>
            <td className="action-cell">
              <a href="about:blank" className="text-danger" onClick={(e: React.MouseEvent) => { e.preventDefault(); handleDelete(index) }}>
                <i className="fas fa-trash-alt"></i> Delete
              </a>
            </td>
          </tr>
        );
        idx++
      }
    }
    return result;
  }

  const handleSave = (scheduleId: number) => {
    times.forEach(t => t.scheduleId = scheduleId);
    const promises: Promise<any>[] = []
    deleteIds.forEach(id => { promises.push(ApiHelper.delete("/scheduleTimes/" + id)) });
    if (times.length > 0) promises.push(ApiHelper.post("/scheduleTimes", times));
    Promise.all(promises)
      .then(() => { props.saveCallback(scheduleId) })
      .catch(err => {
        console.error("Failed to save schedule times:", err);
        ToastHelper.error("Failed to save schedule times");
      });
  }

  const handleParentCommand = () => {
    if (props.pendingCommand) {
      if (props.pendingCommand.action === "save") handleSave(parseInt(props.pendingCommand.parameter, 0))
    }
  }

  const populateSchedule = (name: string) => {
    //delete existing times
    let deleteIdsCopy: number[] = [...deleteIds];
    times.forEach(t => {
      if (t.id && deleteIds.indexOf(t.id) === -1) {
        deleteIdsCopy.push(t.id)
      }
    })
    setDeleteIds(deleteIdsCopy);

    let timesCopy: ScheduleTimeInterface[] = [];
    if (name === "8-5") {
      for (let i = 1; i < 6; i++) timesCopy.push({ dayOfWeek: i, endHour: 17, endMinute: 0, startHour: 8, startMinute: 0, scheduleId: props.scheduleId });
    } else if (name === "24/7") {
      for (let i = 0; i < 7; i++) timesCopy.push({ dayOfWeek: i, endHour: 23, endMinute: 59, startHour: 0, startMinute: 0, scheduleId: props.scheduleId });
    }
    setTimes(timesCopy);
  }

  React.useEffect(init, [props.scheduleId]) // eslint-disable-line
  React.useEffect(handleParentCommand, [props.pendingCommand]) // eslint-disable-line

  return (
    <>
      <div className="section d-flex justify-content-between align-items-center">
        <span>Run Times</span>
        <span>
          <a href="about:blank" onClick={(e) => { e.preventDefault(); populateSchedule("24/7") }}>24/7</a> &middot;&nbsp;
          <a href="about:blank" onClick={(e) => { e.preventDefault(); populateSchedule("8-5") }}>M-F 8-5</a> &middot;&nbsp;
          <a href="about:blank" onClick={(e) => { e.preventDefault(); populateSchedule("") }}>Clear all</a>
        </span>
      </div>
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr><th>Day</th><th>Start Time</th><th>End Time</th><th>Action</th></tr>
          </thead>
          <tbody>
            {getRows()}
            <tr><td colSpan={4} className="action-cell text-end">
              <a href="about:blank" className="text-success" onClick={handleAdd}><i className="fas fa-plus"></i> Add</a>
            </td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
};